import { useState, useEffect } from 'react';
import { X, MessageCircle, Send, Copy, ExternalLink } from 'lucide-react';
import axiosInstance from '../config/axiosConfig';

export default function WhatsAppModal({ isOpen, onClose, candidate, jd = null }) {
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [messagePreview, setMessagePreview] = useState('');
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [whatsappUrl, setWhatsappUrl] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      fetchTemplates();
      setSelectedTemplate('');
      setMessagePreview('');
      setWhatsappUrl('');
      setError('');
    }
  }, [isOpen]);

  useEffect(() => {
    if (selectedTemplate && candidate) {
      generatePreview();
    }
  }, [selectedTemplate, candidate, jd]);

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get('/whatsapp/templates');
      setTemplates(response.data);
      
      // Auto-select first template
      if (response.data.length > 0) {
        setSelectedTemplate(response.data[0].id);
      }
    } catch (error) {
      console.error('Failed to fetch templates:', error);
      setError('Failed to load message templates');
    } finally {
      setLoading(false);
    }
  };

  const generatePreview = async () => {
    if (!selectedTemplate || !candidate) return;

    setGenerating(true);
    setError('');

    try {
      const payload = {
        template_id: selectedTemplate,
        candidate_id: candidate.id,
        jd_id: jd?.id || null
      };

      const response = await axiosInstance.post('/whatsapp/send-link', payload);
      
      setMessagePreview(response.data.message_preview);
      setWhatsappUrl(response.data.whatsapp_url);
    } catch (error) {
      console.error('Failed to generate preview:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to generate message';
      setError(errorMsg);
      setMessagePreview('');
      setWhatsappUrl('');
    } finally {
      setGenerating(false);
    }
  };

  const handleSendWhatsApp = () => {
    if (whatsappUrl) {
      // Open WhatsApp in new tab
      window.open(whatsappUrl, '_blank');
      
      // Close modal after sending
      setTimeout(() => {
        onClose();
      }, 500);
    }
  };

  const copyToClipboard = async () => {
    if (messagePreview) {
      try {
        await navigator.clipboard.writeText(messagePreview);
        // Could add a toast notification here
      } catch (error) {
        console.error('Failed to copy to clipboard:', error);
      }
    }
  };

  if (!isOpen || !candidate) return null;

  return (
    <div 
      className="fixed inset-0 bg-navy/90 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-navy-light border border-teal/30 rounded-lg shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-teal/20">
          <div>
            <h3 className="text-xl font-heading font-semibold text-gold flex items-center">
              <MessageCircle className="mr-2 text-green-500" size={24} />
              Send WhatsApp Message
            </h3>
            <p className="text-sm text-textLight/70 mt-1">
              To: {candidate.name} {candidate.phone && `(${candidate.phone})`}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-textLight/60 hover:text-gold p-1 transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {error && (
            <div className="mb-4 p-3 bg-red-500/20 border border-red-500/50 rounded text-red-400 text-sm">
              {error}
            </div>
          )}

          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-teal border-t-transparent mx-auto mb-2"></div>
              <p className="text-textLight/60 text-sm">Loading templates...</p>
            </div>
          ) : (
            <>
              {/* Template Selection */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-textLight mb-2">
                  Message Template
                </label>
                <select
                  value={selectedTemplate}
                  onChange={(e) => setSelectedTemplate(e.target.value)}
                  className="w-full px-3 py-2 bg-navy border border-teal/30 rounded-lg focus:ring-2 focus:ring-teal focus:border-teal text-textLight"
                >
                  <option value="">Select a template...</option>
                  {templates.map((template) => (
                    <option key={template.id} value={template.id} className="bg-navy text-textLight">
                      {template.name} - {template.use_case}
                    </option>
                  ))}
                </select>
              </div>

              {/* Message Preview */}
              {selectedTemplate && (
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-medium text-textLight">
                      Message Preview
                    </label>
                    {messagePreview && (
                      <button
                        onClick={copyToClipboard}
                        className="text-xs text-teal hover:text-teal-light transition-colors flex items-center space-x-1"
                        title="Copy to clipboard"
                      >
                        <Copy className="w-3 h-3" />
                        <span>Copy</span>
                      </button>
                    )}
                  </div>
                  
                  <div className="bg-navy/50 border border-teal/20 rounded-lg p-4 min-h-[100px]">
                    {generating ? (
                      <div className="flex items-center justify-center py-4">
                        <div className="animate-spin rounded-full h-6 w-6 border-2 border-teal border-t-transparent mr-2"></div>
                        <span className="text-textLight/60 text-sm">Generating message...</span>
                      </div>
                    ) : messagePreview ? (
                      <div className="space-y-2">
                        <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3">
                          <p className="text-textLight text-sm leading-relaxed whitespace-pre-wrap">
                            {messagePreview}
                          </p>
                        </div>
                        <p className="text-xs text-textLight/50">
                          Message will be sent via WhatsApp Web/App
                        </p>
                      </div>
                    ) : (
                      <p className="text-textLight/50 text-sm italic">
                        Select a template to preview the message
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Context Info */}
              <div className="mb-6 p-3 bg-navy/50 border border-teal/20 rounded-lg">
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-textLight/60">Candidate:</span>
                    <span className="ml-2 text-textLight font-medium">{candidate.name}</span>
                  </div>
                  <div>
                    <span className="text-textLight/60">Phone:</span>
                    <span className="ml-2 text-textLight font-medium">
                      {candidate.phone || 'Not available'}
                    </span>
                  </div>
                  {jd && (
                    <div className="col-span-2">
                      <span className="text-textLight/60">Job:</span>
                      <span className="ml-2 text-textLight font-medium">{jd.role_title}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 px-4 py-2 border border-teal/30 rounded-lg text-textLight hover:bg-navy font-medium transition-all"
                  disabled={generating}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSendWhatsApp}
                  disabled={!whatsappUrl || generating || !candidate.phone}
                  className="flex-1 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg flex items-center justify-center space-x-2"
                >
                  <Send className="w-4 h-4" />
                  <span>Send WhatsApp</span>
                  <ExternalLink className="w-4 h-4" />
                </button>
              </div>

              {!candidate.phone && (
                <p className="text-xs text-red-400 mt-2 text-center">
                  No phone number available for this candidate
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
